import { useEffect, useRef, useState } from 'react'
import { Tag, X } from 'lucide-react'
import { validateCoupon, type CouponQuote } from '@/services/orderService'
import { clearPendingCoupon, getPendingCoupon } from '@/lib/promoCoupon'

// ช่องกรอกรหัสคูปองในหน้าชำระเงิน (คอร์ส/หนังสือ) — ส่วนลดคำนวณที่ backend เสมอ (ไม่เชื่อราคาจากหน้าเว็บ)
// ตรวจกับ POST /api/coupons/validate ตอนกด "ใช้คูปอง" แล้วตรวจซ้ำอัตโนมัติเมื่อจำนวน/รุ่นเปลี่ยน (ส่วนลดแบบเปอร์เซ็นต์เปลี่ยนตามยอด)
// ไม่จองสิทธิ์: ตอนกดชำระเงิน backend ตรวจซ้ำอีกครั้ง ถ้าสิทธิ์หมดไปก่อนจะ error และให้ลองใหม่
export default function CouponBox({
  itemType, refId, quantity, batchId, applied, onApplied, disabled,
}: {
  itemType: 'COURSE' | 'BOOK'
  refId: number
  quantity: number
  batchId?: number | null
  applied: CouponQuote | null
  onApplied: (quote: CouponQuote | null) => void
  disabled?: boolean
}) {
  // รหัสที่ผู้ใช้คัดลอกไว้จากแถบ/หน้าต่างโปรโมชัน → กรอกให้ในช่อง (ยังต้องกด "ใช้คูปอง" เอง)
  const [code, setCode] = useState(() => getPendingCoupon())
  const [checking, setChecking] = useState(false)
  const [error, setError] = useState('')
  const lastKey = useRef('')

  const check = async (raw: string) => {
    const value = raw.trim().toUpperCase()
    if (!value) { setError('กรุณากรอกรหัสคูปอง'); return }
    setChecking(true)
    setError('')
    try {
      const quote = await validateCoupon({ code: value, itemType, refId, quantity, batchId })
      onApplied(quote)
      setCode('')
      clearPendingCoupon()
    } catch (err) {
      onApplied(null)
      setError(err instanceof Error ? err.message : 'ตรวจสอบคูปองไม่สำเร็จ')
    } finally {
      setChecking(false)
    }
  }

  // จำนวน/รุ่นเปลี่ยนหลังใช้คูปองแล้ว → คำนวณส่วนลดใหม่ (ถ้าใช้ไม่ได้แล้ว ถอดคูปองออกพร้อมบอกเหตุผล)
  const appliedCode = applied?.code
  useEffect(() => {
    if (!appliedCode) { lastKey.current = ''; return }
    const key = `${appliedCode}|${quantity}|${batchId ?? ''}|${refId}`
    if (lastKey.current === '') { lastKey.current = key; return }   // ครั้งแรกหลังเพิ่งใช้คูปอง ไม่ต้องตรวจซ้ำ
    if (lastKey.current === key) return
    lastKey.current = key
    validateCoupon({ code: appliedCode, itemType, refId, quantity, batchId })
      .then(onApplied)
      .catch((err) => { onApplied(null); setError(err instanceof Error ? err.message : 'คูปองใช้ไม่ได้แล้ว') })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedCode, quantity, batchId, refId])

  if (applied) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-sm border border-emerald-300 bg-emerald-50 px-3 py-2.5 text-sm">
        <span className="flex min-w-0 items-center gap-2 text-emerald-700">
          <Tag className="h-4 w-4 shrink-0" />
          <span className="truncate">คูปอง <b>{applied.code}</b> · ลด ฿{applied.discount.toLocaleString()}</span>
        </span>
        <button
          type="button"
          disabled={disabled}
          onClick={() => { onApplied(null); setError('') }}
          className="shrink-0 rounded p-1 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
          aria-label="ลบคูปอง"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    )
  }

  return (
    <div>
      <label className="mb-1.5 block text-sm text-text-muted">มีคูปองส่วนลด?</label>
      <div className="flex gap-2">
        <input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void check(code) } }}
          placeholder="กรอกรหัสคูปอง"
          maxLength={30}
          disabled={disabled || checking}
          className="w-full rounded-sm border border-border bg-surface-2 px-3.5 py-2.5 text-sm uppercase text-text placeholder:normal-case placeholder:text-text-muted/70 outline-none transition focus:border-indigo-400 disabled:opacity-60"
        />
        <button
          type="button"
          onClick={() => void check(code)}
          disabled={disabled || checking || !code.trim()}
          className="shrink-0 rounded-sm border border-indigo-400 px-4 py-2.5 text-sm font-medium text-indigo-500 transition hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {checking ? 'กำลังตรวจ...' : 'ใช้คูปอง'}
        </button>
      </div>
      {error && <p className="mt-1.5 text-xs text-rose-500">{error}</p>}
    </div>
  )
}
